import React from 'react';
import { X, Share2, Copy, CheckCircle2 } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { copyToClipboard } from '../utils/clipboard';

// v10.0.2 - All direct imports - 2026-03-11 15:00

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: any;
  item?: any; // The post/report to share
}

export function ShareModal({ isOpen, onClose, user, item }: ShareModalProps) {
  const [copied, setCopied] = React.useState(false);

  // Generate shareable link for the specific post
  const getShareableLink = () => {
    if (!item?.id) {
      return 'https://sentlo.com';
    }

    const baseUrl = window.location.origin;
    const params = new URLSearchParams({
      id: item.id,
      type: item.type || 'traffic',
      ...(item.location && { location: item.location }),
      ...(item.description && { description: item.description.substring(0, 100) }), // Limit length
      ...(item.user?.name && { reporter: item.user.name }),
      ...(item.timestamp && { timestamp: item.timestamp }),
    });

    return `${baseUrl}/share/report?${params.toString()}`;
  };

  const shareLink = getShareableLink();

  const shareText = item 
    ? `🚨 Check out this ${item.type || 'report'} on Sentlo!\n\n${item.description || 'Important community update'}\n📍 ${item.location || 'Nigeria'}\n\n👉 View full details: ${shareLink}\n\n🇳🇬 Join Sentlo to stay updated on traffic, weather, and community alerts in real-time!`
    : `🚨 I'm using Sentlo to get real-time traffic, weather, and safety updates in Nigeria! 
 
 🎁 Sign up now and earn 250 Sentlo Coins instantly!
 ✅ Track power outages (Light Watch)
 ✅ Get live traffic updates
 ✅ Join Digital Ajo circles
 ✅ Report emergencies with SOS alerts
 ✅ Check market prices in real-time
 
 Your voice matters! Join thousands of Nigerians making our communities safer and smarter. 🇳🇬
 
 https://sentlo.com`;

  const handleCopy = async () => {
    const success = await copyToClipboard(shareText);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="bg-white rounded-3xl shadow-2xl max-w-md w-full max-h-[85vh] overflow-hidden pointer-events-auto"
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 relative">
                <button 
                  onClick={onClose}
                  className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/30 transition-all"
                >
                  <X className="w-5 h-5" />
                </button>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                    <Share2 className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">
                      {item ? `Share ${item.type || 'Report'}` : 'Share Sentlo'}
                    </h2>
                    <p className="text-sm text-white/80">
                      {item ? 'Help others stay informed!' : 'Spread the word!'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 space-y-4">
                {!user && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <p className="text-sm text-amber-900 font-medium">
                      🌟 <strong>Sign up first</strong> to earn 10 bonus coins for every share!
                    </p>
                  </div>
                )}

                {item && (
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                    <p className="text-xs font-semibold text-green-900 mb-2">📍 Direct Link</p>
                    <p className="text-xs text-green-700 break-all font-mono bg-white px-3 py-2 rounded-lg border border-green-100">
                      {shareLink}
                    </p>
                  </div>
                )}

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-zinc-700">Copy & share this message:</label>
                  <div className="relative">
                    <textarea
                      value={shareText}
                      readOnly
                      className="w-full h-64 p-4 bg-zinc-50 border border-zinc-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                </div>

                <button
                  onClick={handleCopy}
                  className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white py-3 rounded-xl font-bold shadow-lg shadow-green-600/20 active:scale-95 transition-all flex items-center justify-center gap-2"
                >
                  {copied ? (
                    <>
                      <CheckCircle2 className="w-5 h-5" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-5 h-5" />
                      Copy to Clipboard
                    </>
                  )}
                </button>

                <p className="text-xs text-center text-zinc-500">
                  Paste this message on WhatsApp, Twitter, Facebook, or anywhere else!
                </p>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}